import { clsx } from '@alexis/helpers/clsx';
import { dateDisplayFormat } from '@alexis/helpers/date';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import DatePicker from '@components/DatePicker';
import FlightDateSelector from '@components/flights/FlightDateSelector';
import InputSelector from '@components/InputSelector';

import {
  FARE_CALENDAR_ROUNDTRIP_CORE_CONFIG_KEY,
  FLY_TO_ANYWHERE_ALLOWED_OUTBOUND_INBOUND_GAP_DAYS,
  SHOW_FLY_TO_ANYWHERE,
  WEEGIO_JETPACK_IMAGE_URL,
} from '@constants/flight';
import { AUTOCOMPLETE_CORE_CONFIG_KEY } from '@constants/flight';

import { GlobalContext } from '@context/GlobalContext';

import { getDateAfterNDaysFromTheDate } from '@helpers/flights/date';

import ClearIcon from '@icons/close.svg';
import EarthIcon from '@icons/new/earth.svg';
import SwapIcon from '@icons/swap.svg';

import { useGetFlightFareCalendar } from '@hooks/api/flights/useGetFlightFareCalendar';
import { useGetFlightsAutocomplete } from '@hooks/api/places/useGetFlightsAutocomplete';
import { useGetFlightsPopularCities } from '@hooks/api/places/useGetFlightsPopularCities';
import { useGetNearestPlaces } from '@hooks/api/places/useGetNearestPlaces';
import { useGetPlaces } from '@hooks/api/places/useGetPlaces';
import { useClarityCustomTag } from '@hooks/useClarity';

import {
  getCurrentSiteState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
  getUserState,
  getCurrencyState,
} from '@redux/selectors';

import type { FlightSearchType } from '@wegoTypes/flights/flightSearchType';
import { FareCalendarType } from '@wegoTypes/flights/metasearch/calendar';

import {
  formatPlaceLabel,
  formatPlaceLabelForSelectedPlaceFields,
  getFlightFareCalendarParams,
  isValidDate,
  parseDateFromInputString,
} from './helper';
import styles from './LegSearch.module.scss';

export interface LegSearchProps {
  clientId: string | undefined;
  clientSessionId: string | undefined;
  geolocationCoordinates: GeolocationCoordinates | undefined;
  isRemoveLegSearchDisabled: boolean;
  legSearch: LegSearch;
  legSearchIndex: number;
  nextLegSearchOutboundDateMilliseconds: number | undefined;
  onLegSearchFieldChange(
    legSearchIndex: number,
    field: FlightSearchFields,
    value: InboundPlace | LegSearchDate,
  ): void;
  onRemoveLegSearchClick(legSearchIndex: number): void;
  onRemoveReturnDateClick(): void;
  onDateReset(): void;
  pageViewId: string;
  previousLegSearchOutboundDateMilliseconds: number | undefined;
  searchType: FlightSearchType;
  passengerCount: number;
  autoFocusSelectionOnFirstLoad?: boolean;
}

type OutboundInbound = 'outbound' | 'inbound';

type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

const LegSearch = forwardRef<LegSearchHandles, LegSearchProps>(
  (
    {
      clientId,
      clientSessionId,
      geolocationCoordinates,
      isRemoveLegSearchDisabled,
      legSearch,
      legSearchIndex,
      nextLegSearchOutboundDateMilliseconds,
      onLegSearchFieldChange,
      onDateReset,
      onRemoveLegSearchClick,
      onRemoveReturnDateClick,
      pageViewId,
      previousLegSearchOutboundDateMilliseconds,
      searchType,
      passengerCount,
      autoFocusSelectionOnFirstLoad = false,
    },
    ref,
  ) => {
    const user = useSelector(getUserState);
    const translations = useSelector(getTranslationsState);
    const locale = useSelector(getLocaleState);
    const currentSite = useSelector(getCurrentSiteState);
    const isRtl = useSelector(getIsRtlState);

    const { userCentricCoreConfigs } = useContext(GlobalContext);

    //#region search params
    const [searchParams] = useSearchParams();
    const openCalendarSearchParam = searchParams.get('open_calendar');

    //#endregion

    const outboundPlacePickerRef = useRef<HTMLInputElement>(null);
    const inboundPlacePickerRef = useRef<HTMLInputElement>(null);

    const [outboundSearchQuery, setOutboundSearchQuery] = useState<string>('');
    const [outboundSearchQueryPlaces, setOutboundSearchQueryPlaces] = useState<Array<Place>>([]);
    const [outboundSearchActiveItemIndex, setOutboundSearchActiveItemIndex] = useState<number>(0);
    const [inboundSearchQuery, setInboundSearchQuery] = useState<string>('');
    const [inboundSearchQueryPlaces, setInboundSearchQueryPlaces] = useState<Array<Place>>([]);
    const [inboundSearchActiveItemIndex, setInboundSearchActiveItemIndex] = useState<number>(0);

    const [isSwapped, setIsSwapped] = useState<boolean>(false);

    const [selectedDate, setSelectedDate] = useState<Date>(); // State used by date picker component
    const [activeOutboundDateMilliseconds, setActiveOutboundDateMilliseconds] = useState<number>(0);

    const [fromInputValue, setFromInputValue] = useState('');
    const [toInputValue, setToInputValue] = useState('');

    const limitFromMilliseconds$ = new Date().setHours(0, 0, 0, 0);

    //#region Pickers focus state
    const [isOutboundPlacePickerFocus, setIsOutboundPlacePickerFocus] = useState<boolean>(false);
    const [isInboundPlacePickerFocus, setIsInboundPlacePickerFocus] = useState<boolean>(false);

    const [isDatePickerOutboundFocus, setIsDatePickerOutboundFocus] = useState<boolean>(false);
    const [isDatePickerOutboundChanged, setIsDatePickerOutboundChanged] = useState<boolean>(false);
    const [isDatePickerInboundFocus, setIsDatePickerInboundFocus] = useState<boolean>(false);
    const [isDatePickerInboundChanged, setIsDatePickerInboundChanged] = useState<boolean>(false);
    const [isInboundDateInputClicked, setIsInboundDateInputClicked] = useState<boolean>(false);
    //#endregion

    //#region Pickers validated and has error state
    const [isOutboundSearchQueryValidated, setOutboundSearchQueryValidated] =
      useState<boolean>(false);
    const [outboundSearchQueryHasError, setOutboundSearchQueryHasError] = useState<boolean>(false);

    const [isInboundSearchQueryValidated, setInboundSearchQueryValidated] =
      useState<boolean>(false);
    const [inboundSearchQueryHasError, setInboundSearchQueryHasError] = useState<boolean>(false);

    const [isOutboundDateMillisecondsValidated, setIsOutboundDateMillisecondsValidated] =
      useState<boolean>(false);
    const [outboundDateMillisecondsHasError, setOutboundDateMillisecondsHasError] =
      useState<boolean>(false);

    const [isInboundDateMillisecondsValidated, setIsInboundDateMillisecondsValidated] =
      useState<boolean>(false);
    const [inboundDateMillisecondsHasError, setInboundDateMillisecondsHasError] =
      useState<boolean>(false);

    const [showLoadingFareCalendarArrival, setShowLoadingFareCalendarArrival] =
      useState<boolean>(false);
    //#endregion

    const [isShowFareCalendarNote, setIsShowFareCalendarNote] = useState<boolean>(true);

    const [isActiveInboundPlacePicker, setIsActiveInboundPlacePicker] = useState<boolean>(false);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(autoFocusSelectionOnFirstLoad);

    useImperativeHandle(ref, () => ({
      validate,
    }));

    const trackEvent = useCallback(
      (action: string, value: string) => {
        if (clientId && clientSessionId) {
          const eventData = {
            id: pageViewId,
            category: 'search_form_flights',
            object: searchType.toLowerCase(),
            action,
            value,
          };

          genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
        }
      },
      [clientId, clientSessionId, pageViewId, searchType, user?.userHash],
    );

    //#region A/B tests
    const autosuggestCoreConfig = userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === AUTOCOMPLETE_CORE_CONFIG_KEY,
    );
    const autosuggestVariantA = autosuggestCoreConfig?.value === '1';

    useClarityCustomTag(AUTOCOMPLETE_CORE_CONFIG_KEY, autosuggestCoreConfig?.value);

    const currency = useSelector(getCurrencyState);

    const fareCalendarCoreConfig = userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === FARE_CALENDAR_ROUNDTRIP_CORE_CONFIG_KEY,
    );

    const isRoundTripFareCalendarVariant = fareCalendarCoreConfig?.value === '1';
    useClarityCustomTag(FARE_CALENDAR_ROUNDTRIP_CORE_CONFIG_KEY, fareCalendarCoreConfig?.value);

    const isRoundTripFareCalendar = isRoundTripFareCalendarVariant && searchType === 'roundTrip';

    const isEligibleForFareCalendar = searchType === 'oneWay' || isRoundTripFareCalendar;

    const showFlyToAnywhereCoreConfig = userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === SHOW_FLY_TO_ANYWHERE,
    );
    const isVariantShowFlyToAnywhere = showFlyToAnywhereCoreConfig?.value === '1';
    useClarityCustomTag(SHOW_FLY_TO_ANYWHERE, showFlyToAnywhereCoreConfig?.value);

    const isEligibleForFlyToAnywhere = searchType !== 'multiCity' && isVariantShowFlyToAnywhere;
    const shouldShowInboundPlacesInitialOptions =
      !inboundSearchQuery || legSearch.inboundPlace === 'Anywhere';

    const showFlyToAnywhereOption =
      isEligibleForFlyToAnywhere && shouldShowInboundPlacesInitialOptions;
    //#endregion

    const departureCode = legSearch.outboundPlace?.code || '';
    const arrivalCode = (legSearch.inboundPlace as OutboundPlace)?.code || '';

    const {
      data: fareCalendarDeparture,
      isLoading: isLoadingFareCalendarDeparture,
      isError: isFareCalendarErrorDeparture,
    } = useGetFlightFareCalendar(
      getFlightFareCalendarParams(true, legSearch, currency, searchParams),
      {
        enabled: isEligibleForFareCalendar && !!departureCode && !!arrivalCode,
      },
    );

    const {
      data: fareCalendarArrival,
      isLoading: isLoadingFareCalendarArrival,
      isError: isFareCalendarErrorArrival,
    } = useGetFlightFareCalendar(
      getFlightFareCalendarParams(false, legSearch, currency, searchParams),
      {
        enabled: isRoundTripFareCalendar && !!departureCode && !!arrivalCode,
      },
    );

    const fareCalendar = useMemo(() => {
      if (searchType === 'oneWay' || (isRoundTripFareCalendar && isDatePickerOutboundFocus)) {
        return fareCalendarDeparture;
      } else if (
        isRoundTripFareCalendar &&
        !isDatePickerOutboundFocus &&
        !showLoadingFareCalendarArrival
      ) {
        return fareCalendarArrival;
      }

      return undefined;
    }, [
      fareCalendarArrival,
      fareCalendarDeparture,
      searchType,
      isDatePickerOutboundFocus,
      showLoadingFareCalendarArrival,
    ]);

    const isLoadingFareCalendar = useMemo(() => {
      if (searchType === 'oneWay' || (isRoundTripFareCalendar && isDatePickerOutboundFocus)) {
        return isLoadingFareCalendarDeparture;
      } else if (isRoundTripFareCalendar) {
        return isLoadingFareCalendarArrival || showLoadingFareCalendarArrival;
      }
      return false;
    }, [
      isLoadingFareCalendarArrival,
      isLoadingFareCalendarDeparture,
      searchType,
      isDatePickerOutboundFocus,
      showLoadingFareCalendarArrival,
    ]);

    useEffect(() => {
      if (
        !departureCode ||
        !arrivalCode ||
        isFareCalendarErrorDeparture ||
        isFareCalendarErrorArrival ||
        !isEligibleForFareCalendar
      ) {
        setIsShowFareCalendarNote(false);
      } else {
        setIsShowFareCalendarNote(true);
      }
    }, [
      isFareCalendarErrorArrival,
      isFareCalendarErrorDeparture,
      departureCode,
      arrivalCode,
      isEligibleForFareCalendar,
    ]);

    const isShowPriceForRoundTrip = useMemo(() => {
      return (
        isRoundTripFareCalendar &&
        Object.keys(fareCalendarDeparture?.prices || {}).length > 0 &&
        Object.keys(fareCalendarArrival?.prices || {}).length > 0
      );
    }, [fareCalendarDeparture, fareCalendarArrival, isRoundTripFareCalendar]);

    useEffect(() => {
      if (isDatePickerInboundFocus) {
        setShowLoadingFareCalendarArrival(true);

        const timeout = setTimeout(() => {
          setShowLoadingFareCalendarArrival(false);
        }, 200);

        return () => clearTimeout(timeout);
      }
    }, [isDatePickerInboundFocus]);

    // Get place based on outboundSearchQuery
    const { isLoading: isLoadingOutboundFlightsAutocomplete } = useGetFlightsAutocomplete(
      {
        searchFor: 'origin',
        query: outboundSearchQuery,
        minimumAirportCount: 1,
      },
      {
        enabled: Boolean(
          !!outboundSearchQuery && isOutboundPlacePickerFocus && autosuggestVariantA,
        ),
        onSuccessCallback: (data) => setOutboundSearchQueryPlaces(data),
        onErrorCallback: () => setOutboundSearchQueryPlaces([]),
      },
    );

    const { isLoading: isLoadingOutboundPlaces } = useGetPlaces(
      {
        query: outboundSearchQuery,
        isCity: false,
        isDistrict: false,
        isHotel: false,
        isRegion: false,
        isActivity: false,
        isAttraction: false,
        minimumAirportCount: 1,
      },
      {
        enabled: Boolean(
          !!outboundSearchQuery && isOutboundPlacePickerFocus && !autosuggestVariantA,
        ),
        onSuccessCallback: (data) => setOutboundSearchQueryPlaces(data),
        onErrorCallback: () => setOutboundSearchQueryPlaces([]),
      },
    );

    const isLoadingPlacesBasedOnOutbound = useMemo(() => {
      return isLoadingOutboundFlightsAutocomplete || isLoadingOutboundPlaces;
    }, [isLoadingOutboundFlightsAutocomplete, isLoadingOutboundPlaces]);

    // Get place based on inboundSearchQuery
    const { isLoading: isLoadingInboundFlightsAutocomplete } = useGetFlightsAutocomplete(
      {
        searchFor: 'destination',
        query: inboundSearchQuery,
        minimumAirportCount: 1,
      },
      {
        enabled: Boolean(!!inboundSearchQuery && isInboundPlacePickerFocus && autosuggestVariantA),
        onSuccessCallback: (data) => setInboundSearchQueryPlaces(data),
        onErrorCallback: () => setInboundSearchQueryPlaces([]),
      },
    );

    const { isLoading: isLoadingInboundPlaces } = useGetPlaces(
      {
        query: inboundSearchQuery,
        isCity: false,
        isDistrict: false,
        isHotel: false,
        isRegion: false,
        isActivity: false,
        isAttraction: false,
        minimumAirportCount: 1,
      },
      {
        enabled: Boolean(!!inboundSearchQuery && isInboundPlacePickerFocus && !autosuggestVariantA),
        onSuccessCallback: (data) => setInboundSearchQueryPlaces(data),
        onErrorCallback: () => setInboundSearchQueryPlaces([]),
      },
    );

    const isLoadingPlacesBasedOnInbound = useMemo(() => {
      return isLoadingInboundFlightsAutocomplete || isLoadingInboundPlaces;
    }, [isLoadingInboundFlightsAutocomplete, isLoadingInboundPlaces]);

    // Get outbound popular cities
    const { data: outboundPopularCities, isLoading: isLoadingOutboundPopularCities } =
      useGetFlightsPopularCities({ isDeparture: true, perPageCount: 16 });

    // Get inbound popular cities
    const { data: inboundPopularCities, isLoading: isLoadingInboundPopularCities } =
      useGetFlightsPopularCities({ isDeparture: false, perPageCount: 16 });

    // Get nearest airport based on geolocation
    const { data: nearestAirportByGeolocation, isLoading: isLoadingNearestAirportByGeolocation } =
      useGetNearestPlaces(
        {
          isCity: false,
          latitude: geolocationCoordinates?.latitude,
          longitude: geolocationCoordinates?.longitude,
        },
        {
          enabled: Boolean(geolocationCoordinates),
          select: (data) => data[0],
        },
      );

    const limitFromMilliseconds = useMemo(() => {
      if (previousLegSearchOutboundDateMilliseconds) {
        return previousLegSearchOutboundDateMilliseconds;
      }

      if (
        !!legSearch.outboundDateMilliseconds &&
        legSearch.outboundDateMilliseconds !== 'Anytime' &&
        isDatePickerInboundFocus
      ) {
        return legSearch.outboundDateMilliseconds;
      }
      return limitFromMilliseconds$;
    }, [
      isDatePickerInboundFocus,
      legSearch.outboundDateMilliseconds,
      limitFromMilliseconds$,
      previousLegSearchOutboundDateMilliseconds,
    ]);

    const limitToMilliseconds = useMemo(() => {
      const limitTillDate = new Date();
      limitTillDate.setHours(0, 0, 0, 0);

      if (legSearch.inboundPlace === 'Anywhere') {
        if (
          legSearch.outboundDateMilliseconds &&
          legSearch.outboundDateMilliseconds !== 'Anytime' &&
          isDatePickerInboundFocus
        ) {
          return getDateAfterNDaysFromTheDate(
            legSearch.outboundDateMilliseconds,
            FLY_TO_ANYWHERE_ALLOWED_OUTBOUND_INBOUND_GAP_DAYS,
          );
        }

        return limitTillDate.setMonth(limitTillDate.getMonth() + 6);
      }

      limitTillDate.setDate(limitTillDate.getDate() - 1);
      return limitTillDate.setFullYear(limitTillDate.getFullYear() + 1);
    }, [isDatePickerInboundFocus, legSearch.inboundPlace, legSearch.outboundDateMilliseconds]);

    const limitToMillisecondsMulticity = useMemo(() => {
      return nextLegSearchOutboundDateMilliseconds || limitToMilliseconds;
    }, [limitToMilliseconds, nextLegSearchOutboundDateMilliseconds]);

    useEffect(() => {
      const updateOutboundDateMillisecondsOnPickerFocus = () => {
        if (legSearch.outboundDateMilliseconds && isDatePickerOutboundFocus) {
          if (legSearch.outboundDateMilliseconds === 'Anytime') {
            setActiveOutboundDateMilliseconds(limitFromMilliseconds);
          } else {
            setActiveOutboundDateMilliseconds(legSearch.outboundDateMilliseconds);
          }
        } else if (
          legSearch.outboundDateMilliseconds &&
          legSearch.inboundDateMilliseconds &&
          isDatePickerInboundFocus
        ) {
          if (
            legSearch.inboundDateMilliseconds === 'Anytime' ||
            legSearch.outboundDateMilliseconds === 'Anytime'
          ) {
            setActiveOutboundDateMilliseconds(limitFromMilliseconds);
          } else if (legSearch.inboundDateMilliseconds < legSearch.outboundDateMilliseconds) {
            setActiveOutboundDateMilliseconds(legSearch.outboundDateMilliseconds);
          } else {
            setActiveOutboundDateMilliseconds(legSearch.inboundDateMilliseconds);
          }
        }
      };

      const timeout = setTimeout(() => {
        updateOutboundDateMillisecondsOnPickerFocus();
      }, 10);

      return () => clearTimeout(timeout);
    }, [
      isDatePickerInboundFocus,
      isDatePickerOutboundFocus,
      legSearch.inboundDateMilliseconds,
      legSearch.outboundDateMilliseconds,
      limitFromMilliseconds,
    ]);

    //#region validate functions
    const validateOutboundPlace = useCallback(() => {
      let outboundPlaceHasError = false;

      if (!legSearch.outboundPlace) {
        outboundPlaceHasError = true;
      }

      setOutboundSearchQueryHasError(outboundPlaceHasError);
      setOutboundSearchQueryValidated(true);

      return outboundPlaceHasError;
    }, [legSearch.outboundPlace]);

    const validateInboundPlace = useCallback(() => {
      let inboundPlaceHasError = false;

      if (
        !legSearch.inboundPlace ||
        (legSearch.outboundPlace &&
          legSearch.inboundPlace !== 'Anywhere' &&
          legSearch.outboundPlace.cityCode === legSearch.inboundPlace.cityCode)
      ) {
        inboundPlaceHasError = true;
      }

      setInboundSearchQueryHasError(inboundPlaceHasError);
      setInboundSearchQueryValidated(true);

      return inboundPlaceHasError;
    }, [legSearch.inboundPlace, legSearch.outboundPlace]);

    const validateOutboundDateMilliseconds = useCallback(() => {
      let outboundDateMillisecondsHasError = false;

      if (
        !legSearch.outboundDateMilliseconds ||
        (!!previousLegSearchOutboundDateMilliseconds &&
          legSearch.outboundDateMilliseconds !== 'Anytime' &&
          legSearch.outboundDateMilliseconds < previousLegSearchOutboundDateMilliseconds)
      ) {
        outboundDateMillisecondsHasError = true;
      }

      setOutboundDateMillisecondsHasError(outboundDateMillisecondsHasError);
      setIsOutboundDateMillisecondsValidated(true);

      return outboundDateMillisecondsHasError;
    }, [legSearch.outboundDateMilliseconds, previousLegSearchOutboundDateMilliseconds]);

    const validateInboundDateMilliseconds = useCallback(() => {
      let inboundDateMillisecondsHasError = false;

      if (!legSearch.inboundDateMilliseconds && searchType === 'roundTrip') {
        inboundDateMillisecondsHasError = true;
      }

      setInboundDateMillisecondsHasError(inboundDateMillisecondsHasError);
      setIsInboundDateMillisecondsValidated(true);

      return inboundDateMillisecondsHasError;
    }, [legSearch.inboundDateMilliseconds, searchType]);

    const validate = useCallback(() => {
      const outboundPlaceHasError = validateOutboundPlace();
      const inboundPlaceHasError = validateInboundPlace();
      const outboundDateMillisecondsHasError = validateOutboundDateMilliseconds();
      const inboundDateMillisecondsHasError = validateInboundDateMilliseconds();

      return (
        outboundPlaceHasError ||
        inboundPlaceHasError ||
        outboundDateMillisecondsHasError ||
        (searchType === 'roundTrip' && inboundDateMillisecondsHasError)
      );
    }, [
      validateOutboundPlace,
      validateInboundPlace,
      validateOutboundDateMilliseconds,
      validateInboundDateMilliseconds,
      searchType,
    ]);
    //#endregion

    //#region date picker events
    const handleDatePickerOutboundFocus = useCallback(() => {
      setIsOutboundPlacePickerFocus(false);
      setIsInboundPlacePickerFocus(false);

      setIsDatePickerOutboundFocus(true);
      setIsDatePickerInboundFocus(false);

      setIsInboundDateInputClicked(false);

      // Track depart date field opened event
      trackEvent('depart', 'opened');

      document.getElementById('from-input-value')?.focus();
    }, [trackEvent]);

    const handleDatePickerInboundFocus = useCallback(() => {
      setIsOutboundPlacePickerFocus(false);
      setIsInboundPlacePickerFocus(false);

      setIsInboundDateInputClicked(true);

      if (!legSearch.outboundDateMilliseconds) {
        handleDatePickerOutboundFocus();
      } else {
        setIsDatePickerInboundFocus(true);
        setIsDatePickerOutboundFocus(false);

        // Track return date field opened event
        trackEvent('return', 'opened');
      }
      document.getElementById('to-input-value')?.focus();
    }, [handleDatePickerOutboundFocus, legSearch.outboundDateMilliseconds, trackEvent]);

    const handleDateChanged = useCallback(
      (value: Date, field: 'inbound' | 'outbound') => {
        if (field === 'outbound') {
          setIsDatePickerOutboundChanged(true);
          setSelectedDate(value);
          trackEvent('depart', 'selected');
        } else if (field === 'inbound') {
          setIsDatePickerInboundChanged(true);
          setSelectedDate(value);
          trackEvent('return', 'selected');
        }
        setActiveOutboundDateMilliseconds(value.getTime());
      },
      [trackEvent],
    );
    //#endregion

    //#region place picker events
    const handleOutboundPlacePickerChange = useCallback((value: string) => {
      setIsOutboundPlacePickerFocus(true);
      setOutboundSearchQueryValidated(false);
      setOutboundSearchQueryHasError(false);
      setOutboundSearchQuery(value);
      setOutboundSearchActiveItemIndex(0);
    }, []);

    const handleOutboundPlacePickerClear = useCallback(() => {
      setOutboundSearchQuery('');
      setOutboundSearchQueryPlaces([]);
      onLegSearchFieldChange(legSearchIndex, 'outboundPlace', undefined);

      outboundPlacePickerRef.current!.select();
    }, [onLegSearchFieldChange, legSearchIndex, outboundPlacePickerRef.current]);

    const handleOutboundPlacePickerFocus = useCallback(() => {
      setIsDatePickerOutboundFocus(false);
      setIsDatePickerInboundFocus(false);

      setIsOutboundPlacePickerFocus(true);
      setIsInboundPlacePickerFocus(false);

      // Track from field opened event
      trackEvent('from', 'opened');

      outboundPlacePickerRef.current!.select();
    }, [outboundPlacePickerRef, trackEvent]);

    const handlePrevious = useCallback(
      (field: OutboundInbound, step?: number) => {
        if (field === 'outbound' && outboundSearchActiveItemIndex >= 1) {
          setOutboundSearchActiveItemIndex((prev) => Math.max(prev - (step || 1), 0));

          const itemElement = document.getElementById(
            `outboundItem-${outboundSearchActiveItemIndex}`,
          );
          const searchResultContainer = document.getElementById('outboundSearchQueryList');

          if (searchResultContainer && itemElement) {
            const itemElementTop = itemElement.clientHeight * (outboundSearchActiveItemIndex - 1);

            const differences = searchResultContainer.scrollTop - itemElementTop;

            if (differences > 0) {
              searchResultContainer.scrollTop = searchResultContainer.scrollTop - differences;
            }
          }
        }

        if (field === 'inbound' && inboundSearchActiveItemIndex >= 1) {
          setInboundSearchActiveItemIndex((prev) => Math.max(prev - (step || 1), 0));

          const itemElement = document.getElementById(
            `inboundItem-${inboundSearchActiveItemIndex}`,
          );
          const searchResultContainer = document.getElementById('inboundSearchQueryList');

          if (searchResultContainer && itemElement) {
            const itemElementTop = itemElement.clientHeight * (inboundSearchActiveItemIndex - 1);

            const differences = searchResultContainer.scrollTop - itemElementTop;

            if (differences > 0) {
              searchResultContainer.scrollTop = searchResultContainer.scrollTop - differences;
            }
          }
        }
      },
      [inboundSearchActiveItemIndex, outboundSearchActiveItemIndex],
    );

    const handleNext = useCallback(
      (field: OutboundInbound, step?: number) => {
        const outboundVisibleItemsCount = outboundSearchQuery
          ? outboundSearchQueryPlaces.length
          : (outboundPopularCities?.length || 0) + (nearestAirportByGeolocation ? 1 : 0);

        if (
          field === 'outbound' &&
          outboundSearchActiveItemIndex < outboundVisibleItemsCount - (step || 1)
        ) {
          setOutboundSearchActiveItemIndex((prev) => prev + (step || 1));

          const itemElement = document.getElementById(
            `outboundItem-${outboundSearchActiveItemIndex}`,
          );
          const searchResultContainer = document.getElementById('outboundSearchQueryList');
          searchResultContainer;

          if (itemElement && searchResultContainer) {
            const itemElementBottom =
              itemElement.clientHeight * (outboundSearchActiveItemIndex + 2);
            const differences = itemElementBottom - searchResultContainer.clientHeight;

            if (differences > 0 && searchResultContainer.scrollTop <= differences) {
              searchResultContainer.scrollTop = differences;
            }
          }
        }

        const inboundVisibleItemsCount = inboundSearchQuery
          ? inboundSearchQueryPlaces.length
          : (inboundPopularCities?.length || 0) + (showFlyToAnywhereOption ? 1 : 0);
        if (
          field === 'inbound' &&
          inboundSearchActiveItemIndex < inboundVisibleItemsCount - (step || 1)
        ) {
          setInboundSearchActiveItemIndex((prev) => prev + (step || 1));

          const itemElement = document.getElementById(
            `inboundItem-${inboundSearchActiveItemIndex}`,
          );
          const searchResultContainer = document.getElementById('inboundSearchQueryList');
          searchResultContainer;

          if (itemElement && searchResultContainer) {
            const itemElementBottom = itemElement.clientHeight * (inboundSearchActiveItemIndex + 2);
            const differences = itemElementBottom - searchResultContainer.clientHeight;

            if (differences > 0 && searchResultContainer.scrollTop <= differences) {
              searchResultContainer.scrollTop = differences;
            }
          }
        }
      },
      [
        inboundPopularCities?.length,
        inboundSearchActiveItemIndex,
        inboundSearchQuery,
        inboundSearchQueryPlaces.length,
        nearestAirportByGeolocation,
        outboundPopularCities?.length,
        outboundSearchActiveItemIndex,
        outboundSearchQuery,
        outboundSearchQueryPlaces.length,
        showFlyToAnywhereOption,
      ],
    );

    const handleOutboundPlaceSelected = useCallback(
      (place: Place | PlacesFlightsPopularCity, index?: number) => {
        onLegSearchFieldChange(legSearchIndex, 'outboundPlace', place);

        // Track from field selected event
        trackEvent('from', 'selected');

        setIsOutboundPlacePickerFocus(false);
        inboundPlacePickerRef.current!.select();

        if (!legSearch.inboundPlace) {
          setIsInboundPlacePickerFocus(true);
        }

        if (clientId && clientSessionId && index !== undefined) {
          const eventData = {
            category: 'autocomplete',
            object: 'autocomplete_results',
            action: place.type,
            id: pageViewId,
            value: JSON.stringify({
              index_id: index,
              query: outboundSearchQuery,
              name: place.name,
              countryName: place.countryName,
              id: place.id,
              permalink: place.permalink,
              cityId: place.cityId,
            }),
          };
          genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
        }
      },
      [
        onLegSearchFieldChange,
        legSearchIndex,
        legSearch.inboundPlace,
        pageViewId,
        outboundSearchQuery,
        clientId,
        clientSessionId,
        user?.userHash,
        trackEvent,
      ],
    );

    const handleInboundPlacePickerChange = useCallback((value: string) => {
      setIsInboundPlacePickerFocus(true);
      setInboundSearchQueryValidated(false);
      setInboundSearchQueryHasError(false);
      setInboundSearchQuery(value);
      setInboundSearchActiveItemIndex(0);
      setIsFirstLoad(false);
    }, []);

    const handleInboundPlacePickerClear = useCallback(() => {
      setInboundSearchQuery('');
      setInboundSearchQueryPlaces([]);

      onLegSearchFieldChange(
        legSearchIndex,
        'inboundPlace',
        isEligibleForFlyToAnywhere ? 'Anywhere' : undefined,
      );

      inboundPlacePickerRef.current!.select();
    }, [
      onLegSearchFieldChange,
      legSearchIndex,
      inboundPlacePickerRef.current,
      isEligibleForFlyToAnywhere,
    ]);

    const handleInboundPlacePickerFocus = useCallback(() => {
      setIsDatePickerOutboundFocus(false);
      setIsDatePickerInboundFocus(false);
      setIsOutboundPlacePickerFocus(false);
      setIsInboundPlacePickerFocus(true);

      // Track to field opened event
      trackEvent('to', 'opened');

      inboundPlacePickerRef.current!.select();
    }, [inboundPlacePickerRef, trackEvent]);

    const handleInboundPlacePickerBlur = useCallback(() => {
      setIsActiveInboundPlacePicker(false);
    }, []);

    const handleOutboundPlacePickerKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();

          setIsOutboundPlacePickerFocus(false);
        } else if (['Enter', 'Tab'].includes(event.key) && !event.shiftKey) {
          event.preventDefault();

          if (outboundSearchQueryPlaces.length > 0) {
            onLegSearchFieldChange(
              legSearchIndex,
              'outboundPlace',
              outboundSearchQueryPlaces[outboundSearchActiveItemIndex],
            );
          }

          if (!event.currentTarget.value) {
            if (nearestAirportByGeolocation && outboundSearchActiveItemIndex === 0) {
              onLegSearchFieldChange(legSearchIndex, 'outboundPlace', nearestAirportByGeolocation);
            } else if (outboundPopularCities) {
              onLegSearchFieldChange(
                legSearchIndex,
                'outboundPlace',
                outboundPopularCities[
                  nearestAirportByGeolocation
                    ? outboundSearchActiveItemIndex - 1
                    : outboundSearchActiveItemIndex
                ],
              );
            }
          }

          inboundPlacePickerRef.current!.select();
          setIsOutboundPlacePickerFocus(false);

          setOutboundSearchActiveItemIndex(0);
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();

          handlePrevious('outbound', outboundSearchQuery ? 1 : 4);
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();

          handleNext(
            'outbound',
            outboundSearchQuery ||
              (nearestAirportByGeolocation && outboundSearchActiveItemIndex === 0)
              ? 1
              : 4,
          );
        }

        if (event.key === 'ArrowRight' && !outboundSearchQuery) {
          event.preventDefault();
          handleNext('outbound', 1);
        }
        if (event.key === 'ArrowLeft' && !outboundSearchQuery) {
          event.preventDefault();
          handlePrevious('outbound', 1);
        }
      },
      [
        handleNext,
        handlePrevious,
        legSearchIndex,
        nearestAirportByGeolocation,
        onLegSearchFieldChange,
        outboundPopularCities,
        outboundSearchActiveItemIndex,
        outboundSearchQuery,
        outboundSearchQueryPlaces,
      ],
    );

    const handleInboundPlacePickerKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();
          outboundPlacePickerRef.current!.select();
          setIsInboundPlacePickerFocus(false);
        } else if (['Enter', 'Tab'].includes(event.key)) {
          event.preventDefault();

          if (inboundSearchQueryPlaces.length > 0) {
            onLegSearchFieldChange(
              legSearchIndex,
              'inboundPlace',
              inboundSearchQueryPlaces[inboundSearchActiveItemIndex],
            );
          }

          if (!event.currentTarget.value) {
            if (showFlyToAnywhereOption && inboundSearchActiveItemIndex === 0) {
              onLegSearchFieldChange(legSearchIndex, 'inboundPlace', 'Anywhere');
            } else if (inboundPopularCities) {
              onLegSearchFieldChange(
                legSearchIndex,
                'inboundPlace',
                inboundPopularCities[
                  showFlyToAnywhereOption
                    ? inboundSearchActiveItemIndex - 1
                    : inboundSearchActiveItemIndex
                ],
              );
            }
          }

          setInboundSearchActiveItemIndex(0);
          handleDatePickerOutboundFocus();
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();

          handlePrevious('inbound', inboundSearchQuery ? 1 : 4);
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();

          handleNext(
            'inbound',
            inboundSearchQuery || (showFlyToAnywhereOption && inboundSearchActiveItemIndex === 0)
              ? 1
              : 4,
          );
        }

        if (event.key === 'ArrowRight' && !inboundSearchQuery) {
          event.preventDefault();
          handleNext('inbound', 1);
        }
        if (event.key === 'ArrowLeft' && !inboundSearchQuery) {
          event.preventDefault();
          handlePrevious('inbound', 1);
        }
      },
      [
        handleDatePickerOutboundFocus,
        handleNext,
        handlePrevious,
        inboundPopularCities,
        inboundSearchActiveItemIndex,
        inboundSearchQuery,
        inboundSearchQueryPlaces,
        legSearchIndex,
        onLegSearchFieldChange,
        showFlyToAnywhereOption,
      ],
    );

    const handleInboundPlaceSelected = useCallback(
      (place: Place | PlacesFlightsPopularCity | 'Anywhere', index?: number) => {
        onLegSearchFieldChange(legSearchIndex, 'inboundPlace', place);

        // Track to field selected event
        trackEvent('to', 'selected');

        setIsInboundPlacePickerFocus(false);

        if (!legSearch.outboundDateMilliseconds && legSearch.outboundPlace) {
          handleDatePickerOutboundFocus();
        }

        if (!!clientId && !!clientSessionId && index !== undefined && place !== 'Anywhere') {
          const eventData = {
            category: 'autocomplete',
            object: 'autocomplete_results',
            action: place.type,
            id: pageViewId,
            value: JSON.stringify({
              index_id: index,
              query: inboundSearchQuery,
              name: place.name,
              countryName: place.countryName,
              id: place.id,
              permalink: place.permalink,
              cityId: place.cityId,
            }),
          };

          genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
        }
      },
      [
        clientId,
        clientSessionId,
        handleDatePickerOutboundFocus,
        inboundSearchQuery,
        legSearch.outboundDateMilliseconds,
        legSearch.outboundPlace,
        legSearchIndex,
        onLegSearchFieldChange,
        pageViewId,
        trackEvent,
        user?.userHash,
      ],
    );

    const handleMoveDateWithKeyboard = (key: ArrowKey) => {
      switch (key) {
        case 'ArrowRight':
          if (activeOutboundDateMilliseconds >= limitToMilliseconds) {
            return;
          }
          setActiveOutboundDateMilliseconds((prev) => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 1);
            return newDate.getTime();
          });
          break;

        case 'ArrowLeft':
          if (activeOutboundDateMilliseconds <= limitFromMilliseconds) {
            return;
          }
          setActiveOutboundDateMilliseconds((prev) => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 1);
            return newDate.getTime();
          });
          break;
        case 'ArrowUp':
          if (activeOutboundDateMilliseconds <= limitFromMilliseconds + 6 * 24 * 60 * 60 * 1000) {
            return;
          }
          setActiveOutboundDateMilliseconds((prev) => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate.getTime();
          });
          break;
        case 'ArrowDown':
          if (activeOutboundDateMilliseconds >= limitToMilliseconds + 6 * 24 * 60 * 60 * 1000) {
            return;
          }
          setActiveOutboundDateMilliseconds((prev) => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate.getTime();
          });
          break;

        default:
          break;
      }
    };

    const searchButton = document.getElementById('flightSearchFormButton');

    useEffect(() => {
      if (
        activeOutboundDateMilliseconds &&
        isDatePickerOutboundFocus &&
        legSearch.outboundDateMilliseconds !== 'Anytime'
      ) {
        setFromInputValue(
          dateDisplayFormat(
            new Date(activeOutboundDateMilliseconds),
            translations.short_months as Array<string>,
            translations.short_weekdays as Array<string>,
            locale === 'fa',
            5,
          ),
        );
      } else {
        setFromInputValue(
          legSearch.outboundDateMilliseconds
            ? legSearch.outboundDateMilliseconds === 'Anytime'
              ? translations.anytime
              : dateDisplayFormat(
                  new Date(legSearch.outboundDateMilliseconds),
                  translations.short_months as Array<string>,
                  translations.short_weekdays as Array<string>,
                  locale === 'fa',
                  5,
                )
            : '',
        );
      }
    }, [
      activeOutboundDateMilliseconds,
      isDatePickerOutboundFocus,
      legSearch.outboundDateMilliseconds,
      locale,
      translations.anytime,
      translations.short_months,
      translations.short_weekdays,
    ]);

    useEffect(() => {
      if (
        activeOutboundDateMilliseconds &&
        isDatePickerInboundFocus &&
        legSearch.inboundDateMilliseconds !== 'Anytime'
      ) {
        setToInputValue(
          dateDisplayFormat(
            new Date(activeOutboundDateMilliseconds),
            translations.short_months as Array<string>,
            translations.short_weekdays as Array<string>,
            locale === 'fa',
            5,
          ),
        );
      } else {
        setToInputValue(
          legSearch.inboundDateMilliseconds
            ? legSearch.inboundDateMilliseconds === 'Anytime'
              ? translations.anytime
              : dateDisplayFormat(
                  new Date(legSearch.inboundDateMilliseconds),
                  translations.short_months as Array<string>,
                  translations.short_weekdays as Array<string>,
                  locale === 'fa',
                  5,
                )
            : '',
        );
      }
    }, [
      activeOutboundDateMilliseconds,
      isDatePickerInboundFocus,
      legSearch.inboundDateMilliseconds,
      locale,
      translations.anytime,
      translations.short_months,
      translations.short_weekdays,
    ]);

    const handleFromDateKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (!['Enter', 'Tab'].includes(event.key) && !event.shiftKey && !isDatePickerOutboundFocus) {
        handleDatePickerOutboundFocus();
      } else if (event.key === 'Escape') {
        setIsDatePickerOutboundFocus(false);
      } else if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        inboundPlacePickerRef.current!.select();
        setIsDatePickerOutboundFocus(false);
      } else if (['Enter', 'Tab'].includes(event.key) && !event.shiftKey) {
        event.preventDefault();
        setIsDatePickerOutboundChanged(true);
        const date = parseDateFromInputString(
          fromInputValue,
          limitFromMilliseconds,
          limitToMillisecondsMulticity,
        );
        date?.setHours(0, 0, 0, 0);

        if (date && date?.getTime() !== activeOutboundDateMilliseconds) {
          if (isValidDate(date, limitFromMilliseconds, limitToMillisecondsMulticity)) {
            setSelectedDate(date);
            setActiveOutboundDateMilliseconds(date.getTime());
          } else {
            return;
          }
        } else {
          setSelectedDate(new Date(activeOutboundDateMilliseconds));
        }

        if (searchType === 'roundTrip') {
          handleDatePickerInboundFocus();
        } else {
          searchButton?.focus();
        }
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        handleMoveDateWithKeyboard(event.key as ArrowKey);
      }
    };

    const handleToDateKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (!['Enter', 'Tab'].includes(event.key) && !event.shiftKey && !isDatePickerInboundFocus) {
        handleDatePickerInboundFocus();
      } else if (event.key === 'Escape') {
        setIsDatePickerInboundFocus(false);
      } else if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        handleDatePickerOutboundFocus();
      } else if (['Enter', 'Tab'].includes(event.key) && !event.shiftKey) {
        event.preventDefault();
        setIsDatePickerInboundChanged(true);

        const date = parseDateFromInputString(
          toInputValue,
          limitFromMilliseconds,
          limitToMillisecondsMulticity,
        );
        date?.setHours(0, 0, 0, 0);

        if (date && date?.getTime() !== activeOutboundDateMilliseconds) {
          if (isValidDate(date, limitFromMilliseconds, limitToMillisecondsMulticity)) {
            setSelectedDate(date);
            setActiveOutboundDateMilliseconds(date.getTime());
          } else {
            return;
          }
        } else {
          setSelectedDate(new Date(activeOutboundDateMilliseconds));
        }

        searchButton?.focus();
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        handleMoveDateWithKeyboard(event.key as ArrowKey);
      }
    };

    const handleSwapOutboundInboundPlace = useCallback(() => {
      setIsSwapped((prev) => !prev);
      onLegSearchFieldChange(legSearchIndex, 'swapOutboundInboundPlace', undefined);
    }, [onLegSearchFieldChange, legSearchIndex]);
    //#endregion

    const getPlaceDisplay = useCallback(
      (place: Place) => {
        return (
          <>
            {place.type === 'city' ? <i className={clsx(styles.icon, styles.cityGrey)}></i> : null}
            {place.type === 'airport' ? (
              <i className={clsx(styles.icon, styles.airportGrey)}></i>
            ) : null}

            <div className={styles.labelAndCode}>
              <div className={styles.label}>{formatPlaceLabel(place)}</div>

              <div className={styles.code}>
                {place.type === 'city' && place.airportCount! > 1
                  ? translations.all_airports
                  : place.code}
              </div>
            </div>
          </>
        );
      },
      [translations.all_airports],
    );

    const closeAllInputPickers = useCallback((): void => {
      if (isOutboundPlacePickerFocus) {
        setIsOutboundPlacePickerFocus(false);
        setOutboundSearchActiveItemIndex(0);
      }

      if (isInboundPlacePickerFocus) {
        setIsInboundPlacePickerFocus(false);
        setInboundSearchActiveItemIndex(0);
      }

      if (isDatePickerOutboundFocus) {
        setIsDatePickerOutboundFocus(false);
      }

      if (isDatePickerInboundFocus) {
        setIsDatePickerInboundFocus(false);
      }
    }, [
      isDatePickerInboundFocus,
      isDatePickerOutboundFocus,
      isInboundPlacePickerFocus,
      isOutboundPlacePickerFocus,
    ]);

    const handleRemoveReturnDateClick = useCallback(() => {
      closeAllInputPickers();
      onRemoveReturnDateClick();
    }, [onRemoveReturnDateClick, closeAllInputPickers]);

    const handleDateSelected = useCallback(
      (date: Date, priceType?: FareCalendarType) => {
        trackEvent(
          isDatePickerInboundFocus ? 'return' : 'depart',
          priceType === 'CHEAP'
            ? 'cheap_price'
            : priceType === 'NORMAL'
            ? 'average_price'
            : priceType === 'EXPENSIVE'
            ? 'expensive_price'
            : 'selected',
        );

        const selectedDateMilliseconds = date.getTime();

        if (
          selectedDateMilliseconds < limitFromMilliseconds$ ||
          selectedDateMilliseconds > limitToMillisecondsMulticity
        ) {
          return;
        }

        setActiveOutboundDateMilliseconds(selectedDateMilliseconds);
        setSelectedDate(date);
      },
      [isDatePickerInboundFocus, limitFromMilliseconds$, limitToMillisecondsMulticity, trackEvent],
    );

    const handleAnytimeClick = (field: 'inbound' | 'outbound') => {
      if (field === 'inbound') {
        onLegSearchFieldChange(legSearchIndex, 'inboundDateMilliseconds', 'Anytime');
        setIsDatePickerInboundFocus(false);
      } else {
        onLegSearchFieldChange(legSearchIndex, 'outboundDateMilliseconds', 'Anytime');
        setIsDatePickerOutboundFocus(false);
      }
    };

    // Open calendar if open_calendar search param exist
    useEffect(() => {
      if (openCalendarSearchParam !== null) {
        const setDatePickerFocusTimeout: NodeJS.Timeout = setTimeout(() => {
          handleDatePickerOutboundFocus();
        }, 1500);

        return () => {
          clearTimeout(setDatePickerFocusTimeout);
        };
      }
    }, [handleDatePickerOutboundFocus, openCalendarSearchParam]);

    useEffect(() => {
      if (!isInboundPlacePickerFocus && !isDatePickerInboundFocus) {
        setIsInboundDateInputClicked(false);
      }
    }, [isInboundPlacePickerFocus, isDatePickerInboundFocus]);

    useEffect(() => {
      setOutboundSearchQueryValidated(false);
      setOutboundSearchQueryHasError(false);

      setInboundSearchQueryValidated(false);
      setInboundSearchQueryHasError(false);

      setIsOutboundDateMillisecondsValidated(false);
      setOutboundDateMillisecondsHasError(false);

      setIsInboundDateMillisecondsValidated(false);
      setInboundDateMillisecondsHasError(false);
    }, [searchType]);

    useEffect(() => {
      if (
        (isDatePickerOutboundFocus ||
          isDatePickerInboundFocus ||
          isDatePickerOutboundChanged ||
          isDatePickerInboundChanged) &&
        selectedDate
      ) {
        const selectedDateMilliseconds = selectedDate.getTime();

        if (
          selectedDateMilliseconds < limitFromMilliseconds$ ||
          selectedDateMilliseconds > limitToMillisecondsMulticity
        ) {
          return;
        }

        if (isDatePickerOutboundFocus || isDatePickerOutboundChanged) {
          onLegSearchFieldChange(
            legSearchIndex,
            'outboundDateMilliseconds',
            selectedDateMilliseconds,
          );

          setIsOutboundDateMillisecondsValidated(false);
          setOutboundDateMillisecondsHasError(false);
          setIsDatePickerOutboundFocus(false);

          if (searchType === 'roundTrip' && !isDatePickerOutboundChanged) {
            handleDatePickerInboundFocus();
          }
        } else if (isDatePickerInboundFocus || isDatePickerInboundChanged) {
          if (
            legSearch?.outboundDateMilliseconds &&
            legSearch.outboundDateMilliseconds !== 'Anytime' &&
            selectedDateMilliseconds < legSearch.outboundDateMilliseconds
          ) {
            onLegSearchFieldChange(
              legSearchIndex,
              'outboundDateMilliseconds',
              selectedDateMilliseconds,
            );
            setIsOutboundDateMillisecondsValidated(false);
            setOutboundDateMillisecondsHasError(false);
            setIsDatePickerOutboundFocus(false);

            if (searchType === 'roundTrip') {
              handleDatePickerInboundFocus();
            }
          } else {
            onLegSearchFieldChange(
              legSearchIndex,
              'inboundDateMilliseconds',
              selectedDateMilliseconds,
            );
            setIsInboundDateMillisecondsValidated(false);
            setInboundDateMillisecondsHasError(false);
            setIsDatePickerOutboundFocus(false);
            setIsDatePickerInboundFocus(false);
          }
        }

        setIsDatePickerOutboundChanged(false);
        setIsDatePickerInboundChanged(false);
        setSelectedDate(undefined);
      }
    }, [
      handleDatePickerInboundFocus,
      isDatePickerInboundChanged,
      isDatePickerInboundFocus,
      isDatePickerOutboundChanged,
      isDatePickerOutboundFocus,
      legSearch.outboundDateMilliseconds,
      legSearchIndex,
      limitFromMilliseconds$,
      limitToMillisecondsMulticity,
      onLegSearchFieldChange,
      searchType,
      selectedDate,
    ]);

    // Set all picker focus to false if body is clicked
    useEffect(() => {
      if (
        isOutboundPlacePickerFocus ||
        isInboundPlacePickerFocus ||
        isDatePickerOutboundFocus ||
        isDatePickerInboundFocus
      ) {
        const timerId = setTimeout(() => {
          document.body.addEventListener('click', closeAllInputPickers);
        }, 0);

        return () => {
          clearTimeout(timerId);
          document.body.removeEventListener('click', closeAllInputPickers);
        };
      }
    }, [
      closeAllInputPickers,
      isOutboundPlacePickerFocus,
      isInboundPlacePickerFocus,
      isDatePickerOutboundFocus,
      isDatePickerInboundFocus,
    ]);

    useEffect(() => {
      document.body.tabIndex = 1;
    }, []);

    useEffect(() => {
      if (!isOutboundPlacePickerFocus) {
        if (legSearch.outboundPlace) {
          setOutboundSearchQuery(formatPlaceLabelForSelectedPlaceFields(legSearch.outboundPlace));
        } else {
          setOutboundSearchQuery('');
        }
      }
      setOutboundSearchQueryValidated(false);
      setOutboundSearchQueryHasError(false);
    }, [isOutboundPlacePickerFocus, legSearch.outboundPlace]);

    useEffect(() => {
      if (!isInboundPlacePickerFocus) {
        if (legSearch.inboundPlace) {
          setInboundSearchQuery(
            legSearch.inboundPlace === 'Anywhere'
              ? translations.flight_search_destination_anywhere
              : formatPlaceLabelForSelectedPlaceFields(legSearch.inboundPlace),
          );
        } else {
          setInboundSearchQuery('');
        }
      }
      setInboundSearchQueryValidated(false);
      setInboundSearchQueryHasError(false);
    }, [
      isInboundPlacePickerFocus,
      legSearch.inboundPlace,
      translations.flight_search_destination_anywhere,
    ]);

    // Auto-focus the destination field when it becomes active
    useEffect(() => {
      const isDestinationActive =
        !isInboundPlacePickerFocus &&
        !inboundSearchQuery &&
        !legSearch.inboundPlace &&
        !!legSearch.outboundPlace &&
        !isDatePickerInboundFocus;

      if (isDestinationActive && inboundPlacePickerRef.current) {
        // Small delay to ensure DOM is updated
        const timeoutId = setTimeout(() => {
          // Re-evaluate conditions when timeout executes
          const isStillDestinationActive =
            !isInboundPlacePickerFocus &&
            !inboundSearchQuery &&
            !legSearch.inboundPlace &&
            !!legSearch.outboundPlace;

          if (isStillDestinationActive && inboundPlacePickerRef.current) {
            inboundPlacePickerRef.current.focus();
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }, [
      isInboundPlacePickerFocus,
      inboundSearchQuery,
      legSearch.inboundPlace,
      legSearch.outboundPlace,
      isDatePickerInboundFocus,
    ]);

    // Auto focus and select text if field has value on first load or when prop is updated from parent
    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const hasInboundValue = legSearch.inboundPlace && legSearch.inboundPlace !== 'Anywhere';

      // Check if this is first load OR if the value was updated from parent (not user input)
      // We detect parent update by checking if the field is focused but not active
      const isParentUpdate =
        hasInboundValue && document.activeElement === inboundPlacePickerRef.current;

      if ((isFirstLoad && hasInboundValue && searchType !== 'multiCity') || isParentUpdate) {
        timeoutId = setTimeout(() => {
          if (inboundPlacePickerRef.current) {
            inboundPlacePickerRef.current.focus();
            inboundPlacePickerRef.current.select();
          }
          setIsFirstLoad(false);
        }, 100);
      }

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [legSearch.inboundPlace, isFirstLoad, searchType, isActiveInboundPlacePicker]);

    // Event listener for focus inboundPlacePicker
    useEffect(() => {
      const handleFocus = () => {
        setIsActiveInboundPlacePicker(true);
      };

      const inputElement = inboundPlacePickerRef.current;
      if (inputElement) {
        inputElement.addEventListener('focus', handleFocus);

        return () => {
          inputElement.removeEventListener('focus', handleFocus);
        };
      }
    }, []);

    return (
      <div
        className={clsx(
          styles.container,
          searchType === 'roundTrip' && styles.roundTrip,
          searchType === 'oneWay' && styles.oneWay,
        )}
        data-testid='legSearchContainer'
        data-is-loading={`${
          isLoadingInboundPopularCities ||
          isLoadingOutboundPopularCities ||
          isLoadingPlacesBasedOnInbound ||
          isLoadingPlacesBasedOnOutbound ||
          isLoadingNearestAirportByGeolocation
        }`}
      >
        <div className={styles.placePickersContainer}>
          <div className={styles.placePickerContainer} data-testid='outboundPlacePickerContainer'>
            <InputSelector
              dataPw={`${
                outboundSearchQueryPlaces.length === 0 &&
                outboundPopularCities &&
                outboundPopularCities.length > 0
                  ? 'locationPicker_popularCities'
                  : !!outboundSearchQuery && outboundSearchQueryPlaces.length > 0
                  ? 'locationPicker_placeSuggestions'
                  : ''
              }`}
              activeDescendant={`outboundItem-${outboundSearchActiveItemIndex}`}
              listAriaLabel='Suggested origin cities/airports'
              inputAriaLabel='Where from?'
              data-testid='outbound-search-input'
              className={styles.inputSelector}
              hasError={outboundSearchQueryHasError}
              id='outboundSearchQuery'
              isFlightProduct
              isFocus={isOutboundPlacePickerFocus}
              listContainerId='outboundSearchQueryList'
              isValidated={isOutboundSearchQueryValidated}
              onChange={handleOutboundPlacePickerChange}
              onClear={handleOutboundPlacePickerClear}
              onFocus={handleOutboundPlacePickerFocus}
              onKeyDown={handleOutboundPlacePickerKeyDown}
              placeholder={translations.lbl_from}
              ref={outboundPlacePickerRef}
              value={outboundSearchQuery}
            >
              {/* Nearby and Popular Cities */}
              {!outboundSearchQuery ? (
                <>
                  {nearestAirportByGeolocation ? (
                    <>
                      <div className={clsx(styles.category, styles.with8TopMargin)}>
                        {translations.nearby}
                      </div>

                      <li
                        id='outboundItem-0'
                        className={clsx(
                          styles.place,
                          styles.with8TopMargin,
                          outboundSearchActiveItemIndex === 0 && styles.active,
                        )}
                        onClick={() => handleOutboundPlaceSelected(nearestAirportByGeolocation, 0)}
                      >
                        {getPlaceDisplay(nearestAirportByGeolocation)}
                      </li>
                    </>
                  ) : null}

                  {outboundPopularCities && outboundPopularCities.length > 0 ? (
                    <>
                      <div className={clsx(styles.category, styles.with16TopMargin)}>
                        {translations.popular_cities}
                      </div>

                      <div className={styles.popularCities}>
                        {outboundPopularCities &&
                          outboundPopularCities.map((popularCity, index) => {
                            const realIndex = nearestAirportByGeolocation ? index + 1 : index;

                            return (
                              <li
                                key={popularCity.code}
                                id={`outboundItem-${realIndex}`}
                                data-testid='popular-city'
                                className={clsx(
                                  styles.popularCity,
                                  outboundSearchActiveItemIndex === realIndex && styles.active,
                                )}
                                onClick={() => handleOutboundPlaceSelected(popularCity, realIndex)}
                              >
                                <div>{popularCity.cityName}</div>
                              </li>
                            );
                          })}
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}

              {/* Place */}
              {!!outboundSearchQuery && outboundSearchQueryPlaces.length > 0 ? (
                <>
                  {outboundSearchQueryPlaces.map((outboundSearchQueryPlace, index) => (
                    <li
                      role='option'
                      data-pw='locationPicker_placeSuggestion'
                      key={index}
                      id={`outboundItem-${index}`}
                      className={clsx(
                        styles.place,
                        outboundSearchActiveItemIndex === index && styles.active,
                      )}
                      onClick={() => handleOutboundPlaceSelected(outboundSearchQueryPlace, index)}
                    >
                      {getPlaceDisplay(outboundSearchQueryPlace)}
                    </li>
                  ))}
                </>
              ) : null}
            </InputSelector>
          </div>

          <button
            aria-label='Swap origin and destination'
            className={clsx(isSwapped && styles.swap)}
            disabled={
              !legSearch.outboundPlace ||
              !legSearch.inboundPlace ||
              legSearch.inboundPlace === 'Anywhere'
            }
            type='button'
            onClick={handleSwapOutboundInboundPlace}
          >
            <SwapIcon />
          </button>

          <div className={styles.placePickerContainer} data-testid='inboundPlacePickerContainer'>
            <InputSelector
              dataPw={`${
                inboundSearchQueryPlaces.length === 0 &&
                inboundPopularCities &&
                inboundPopularCities.length > 0
                  ? 'locationPicker_popularCities'
                  : !!inboundSearchQuery && inboundSearchQueryPlaces.length > 0
                  ? 'locationPicker_placeSuggestions'
                  : ''
              }`}
              activeDescendant={`inboundItem-${inboundSearchActiveItemIndex}`}
              listAriaLabel='Suggested destination cities/airports'
              inputAriaLabel='Where to?'
              className={clsx(styles.inputSelector, styles.inbound)}
              hasError={inboundSearchQueryHasError}
              id='inboundSearchQuery'
              isFlightInbound={true}
              isFlightProduct
              isFocus={isInboundPlacePickerFocus}
              listContainerId='inboundSearchQueryList'
              isValidated={isInboundSearchQueryValidated}
              isActive={isActiveInboundPlacePicker}
              onChange={handleInboundPlacePickerChange}
              onClear={handleInboundPlacePickerClear}
              onFocus={handleInboundPlacePickerFocus}
              onBlur={handleInboundPlacePickerBlur}
              onKeyDown={handleInboundPlacePickerKeyDown}
              placeholder={translations.lbl_to}
              ref={inboundPlacePickerRef}
              value={inboundSearchQuery}
            >
              {showFlyToAnywhereOption && (
                <div
                  className={clsx(
                    styles.flyToAnywhereWrapper,
                    inboundSearchActiveItemIndex === 0 && styles.active,
                  )}
                  onClick={() => handleInboundPlaceSelected('Anywhere')}
                >
                  <EarthIcon />
                  <div className={styles.text}>
                    {translations.flight_search_destination_anywhere_cta}
                    <img src={WEEGIO_JETPACK_IMAGE_URL} alt='weegio jetpack' />
                  </div>
                </div>
              )}

              {/* Popular Cities */}
              {shouldShowInboundPlacesInitialOptions ? (
                <>
                  {inboundPopularCities && inboundPopularCities.length > 0 ? (
                    <>
                      <div className={clsx(styles.category, styles.with16TopMargin)}>
                        {translations.popular_cities}
                      </div>

                      <div className={styles.popularCities}>
                        {inboundPopularCities &&
                          inboundPopularCities.map((popularCity, index) => {
                            const realIndex = showFlyToAnywhereOption ? index + 1 : index;

                            return (
                              <li
                                key={popularCity.code}
                                id={`inboundItem-${realIndex}`}
                                data-testid='popular-city'
                                className={clsx(
                                  styles.popularCity,
                                  inboundSearchActiveItemIndex === realIndex && styles.active,
                                )}
                                onClick={() => handleInboundPlaceSelected(popularCity, realIndex)}
                              >
                                <div>{popularCity.cityName}</div>
                              </li>
                            );
                          })}
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}

              {/* Place */}
              {!!inboundSearchQuery && inboundSearchQueryPlaces.length > 0 ? (
                <>
                  {inboundSearchQueryPlaces.map((inboundSearchQueryPlace, index) => (
                    <li
                      data-pw='locationPicker_placeSuggestion'
                      key={index}
                      id={`inboundItem-${index}`}
                      className={clsx(
                        styles.place,
                        inboundSearchActiveItemIndex === index && styles.active,
                      )}
                      onClick={() => handleInboundPlaceSelected(inboundSearchQueryPlace, index)}
                    >
                      {getPlaceDisplay(inboundSearchQueryPlace)}
                    </li>
                  ))}
                </>
              ) : null}
            </InputSelector>
          </div>
        </div>

        <div className={styles.datePickerContainer} data-testid='datePickerContainer'>
          <FlightDateSelector
            clientId={clientId}
            clientSessionId={clientSessionId}
            className={styles.flightDateSelector}
            fromHasError={outboundDateMillisecondsHasError}
            fromMillisecond={
              legSearch.outboundDateMilliseconds !== 'Anytime'
                ? legSearch.outboundDateMilliseconds
                : undefined
            }
            fromValue={
              legSearch.outboundDateMilliseconds
                ? dateDisplayFormat(
                    new Date(legSearch.outboundDateMilliseconds),
                    translations.short_months as Array<string>,
                    translations.short_weekdays as Array<string>,
                    locale === 'fa',
                    5,
                  )
                : ''
            }
            isFromFocus={isDatePickerOutboundFocus}
            isFromValidated={isOutboundDateMillisecondsValidated}
            searchType={searchType}
            isToFocus={isDatePickerInboundFocus}
            isToValidated={isInboundDateMillisecondsValidated}
            limitFromMilliseconds={limitFromMilliseconds}
            limitTillMilliseconds={limitToMillisecondsMulticity}
            onDateChanged={handleDateChanged}
            onDateReset={onDateReset}
            onFromFocus={handleDatePickerOutboundFocus}
            fromInputValue={fromInputValue}
            setFromInputValue={setFromInputValue}
            toInputValue={toInputValue}
            setToInputValue={setToInputValue}
            onToFocus={handleDatePickerInboundFocus}
            onFromDateKeyDown={handleFromDateKeyDown}
            onToDateKeyDown={handleToDateKeyDown}
            onRemoveReturnDateClick={handleRemoveReturnDateClick}
            onAnytimeClick={handleAnytimeClick}
            pageViewId={pageViewId}
            toHasError={inboundDateMillisecondsHasError}
            toMillisecond={
              legSearch.inboundDateMilliseconds !== 'Anytime'
                ? legSearch.inboundDateMilliseconds
                : undefined
            }
            toValue={
              legSearch.inboundDateMilliseconds
                ? dateDisplayFormat(
                    new Date(legSearch.inboundDateMilliseconds),
                    translations.short_months as Array<string>,
                    translations.short_weekdays as Array<string>,
                    locale === 'fa',
                    5,
                  )
                : ''
            }
            isShowFareCalendarNote={isShowFareCalendarNote}
            departure={legSearch.outboundPlace}
            arrival={legSearch.inboundPlace}
            isShowPriceForRoundTrip={isShowPriceForRoundTrip}
          >
            <DatePicker
              firstDayOfWeek={
                /^(?:BD|BH|DZ|EG|JO|KW|OM|QA|SA|TN)$/i.test(currentSite.countryCode) ? 7 : 1
              }
              isRtl={isRtl}
              isToInputClicked={isInboundDateInputClicked}
              limitFromMilliseconds={limitFromMilliseconds}
              limitTillMilliseconds={limitToMilliseconds}
              locale={locale}
              onDateSelected={handleDateSelected}
              selectedFromDateMilliseconds={
                legSearch.outboundDateMilliseconds === 'Anytime'
                  ? undefined
                  : legSearch.outboundDateMilliseconds
              }
              selectedToDateMilliseconds={
                legSearch.inboundDateMilliseconds === 'Anytime'
                  ? undefined
                  : legSearch.inboundDateMilliseconds
              }
              translations={translations}
              isFlightDateSelector
              isLoadingFareCalendar={isLoadingFareCalendar}
              calendarFares={fareCalendar}
              passengerCount={passengerCount}
              activeOutboundDateMilliseconds={activeOutboundDateMilliseconds}
              searchType={searchType}
              forcedDisabled={
                legSearch.inboundPlace === 'Anywhere' &&
                legSearch.outboundDateMilliseconds === 'Anytime' &&
                isDatePickerInboundFocus
              }
            />
          </FlightDateSelector>
        </div>

        {searchType === 'multiCity' && !!onRemoveLegSearchClick ? (
          <button
            aria-label='Remove'
            data-pw='multicitySearch_removeLegBtn'
            type='button'
            disabled={isRemoveLegSearchDisabled}
            onClick={() => onRemoveLegSearchClick(legSearchIndex)}
          >
            <ClearIcon className={styles.icon} />
          </button>
        ) : null}
      </div>
    );
  },
);

LegSearch.displayName = 'LegSearch';

export default LegSearch;
